<template>
  <div
    class="entity"
    :class="{
      '-warning': isWarning,
      '--1': level === 1,
      '--2': level === 2,
      '--3': level === 3,
      '-hasChildren': hasChildren,
    }"
    v-hide="{ hide: entity.removed }"
    @hideComplete="hideComplete"
  >
    <div ref="entityRow" class="entity__row" :class="{ 'bg-light-warning': isWarning }">
      <button class="entity__grip">
        <FontAwesomeIcon icon="fa-light fa-grip-lines-vertical" />
      </button>
      <div class="entity__column -code">
        {{ entity.code }}
      </div>
      <div class="entity__column -label">
        <span
          class="me-1"
          v-if="isWarning"
          v-tooltip="{
            title: $t('training.program.ai_confidence') + ' ' + entity.confidenceScore + '%',
            placement: 'top',
          }"
        >
          <FontAwesomeIcon icon="fa-light fa-exclamation-triangle" class="text-warning" />
        </span>
        <span class="d-md-none d-inline" v-if="entity.code">{{ entity.code }} - </span>
        <span
          class="entity__type"
          :class="{ 'bg-light-info': type === 'unit', 'bg-light-success': type === 'subject' }"
          >{{ type === "unit" ? "UE" : "M" }}</span
        >
        {{ entity.name }}
      </div>
      <div class="entity__column -type">
        <span class="badge bg-light-info">
          {{ entity.type }}
        </span>
      </div>
      <div class="entity__column -obl">
        <span class="d-md-none d-inline me-2">{{ $t("training.program.required") }}</span>
        <span
          class="entity__obl"
          :class="{
            '-opt': !entity.mandatory,
            'bg-light-warning': !entity.mandatory,
            'bg-light-info': entity.mandatory,
          }"
        ></span>
      </div>
      <div class="entity__column -hours">
        <span v-if="entity.totalHours">{{ entity.totalHours }}h</span>
      </div>
      <div class="entity__column -ects">
        <span v-if="entity.ectsCredit"
          >{{ entity.ectsCredit }}
          <span class="d-md-none d-inline" v-if="entity.ectsCredit">{{ $t("training.program.ects") }}</span></span
        >
      </div>
      <div class="entity__column -coef">
        <span class="d-md-none d-inline" v-if="entity.coefficient">{{ $t("training.program.coefficient_short") }} </span
        >{{ entity.coefficient ? entity.coefficient : "-" }}
      </div>
      <div class="entity__column -actions">
        <button
          class="btn-icon btnEdit me-2"
          @click="edit"
          v-tooltip="{
            title: stringManager().globalTranslation('edit', type, 'studea_manager', 1, true),
          }"
        >
          <FontAwesomeIcon icon="fa-light fa-pen" />
        </button>
        <button
          class="btn-icon btnRemove"
          @click="remove"
          v-tooltip="{
            title: stringManager().globalTranslation('delete', type, 'studea_manager', 1, true),
          }"
        >
          <FontAwesomeIcon icon="fa-light fa-trash" />
        </button>
      </div>
      <button
        v-if="(hasChildren || hasSubjects) && type === 'unit' && level === 1"
        class="btn-icon entity__expand"
        @click="collapse"
        v-tooltip="{ title: $t('global.see_more') }"
      >
        <FontAwesomeIcon :icon="'fa-light fa-chevron-' + (!entity.collapsed ? 'up' : 'down')" />
      </button>
      <BlockLoader :loading="loading || entity.loading" size="small" />
    </div>
  </div>
</template>

<script>
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { trainingManager } from "@/services/training/training-manager";
import { stringManager } from "@/services/utilities/string-manager";

export default {
  name: "TrainingModelRowComponent",
  components: { FontAwesomeIcon, BlockLoader },
  emits: ["edit", "removed", "hideComplete", "collapse"],
  props: {
    entity: {
      type: Object,
      required: true,
    },
    level: {
      type: Number,
      required: false,
      default: 0,
    },
    type: {
      type: String,
      required: false,
      default: "unit",
    },
    parent: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    hasChildren() {
      return this.entity.childTrainingUnits && Object.keys(this.entity.childTrainingUnits).length;
    },

    hasSubjects() {
      return this.entity.trainingSubjects && Object.keys(this.entity.trainingSubjects).length;
    },

    isWarning() {
      return this.entity.confidenceScore && this.entity.confidenceScore < 75;
    },
  },
  methods: {
    stringManager() {
      return stringManager;
    },

    edit() {
      this.$emit("edit", { object: this.entity, type: this.type, level: this.level, parent: this.parent });
    },

    rowHeight() {
      if (this.$refs.entityRow && this.$refs.entityRow.offsetHeight) {
        return this.$refs.entityRow.offsetHeight;
      }
    },

    async remove() {
      this.loading = true;
      const done = await trainingManager.remove(this.entity.id, this.$route.params.trainingId, this.type, this.type);
      if (done && done.success) {
        this.removed({ object: this.entity, parent: this.parent, type: this.type });
      }
      this.loading = false;
    },

    hideComplete() {
      this.$emit("hideComplete", { object: this.entity, parent: this.parent, type: this.type });
    },

    removed(payload) {
      this.$emit("removed", payload);
    },

    collapse() {
      this.$emit("collapse", this.entity);
    },

    sort(e) {
      if (e.moved) {
        const id = e.moved.element.id;
        const position = e.moved.newIndex;
        trainingManager.sort(id, this.$route.params.trainingId, position, this.type, this.type);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.entity {
  &.--1 {
    &.-hasChildren {
      background-color: #f8f8f8;
      @include dark-theme {
        color: $white;
        background-color: $m-color_9;
      }
    }
  }

  &.--2 {
    .entity {
      &__column {
        &.-label {
          padding-left: 25px;

          @include up-to-md {
            padding-left: 0;
          }
        }
        &.-code {
          padding-left: 25px;
        }
      }

      &__grip {
        left: 20px;

        @include up-to-md {
          left: 0;
        }
      }

      &__row {
        background-color: $white;

        &:hover {
          background-color: #f8f8f8;

          @include dark-theme {
            background-color: $m-color_9;
          }
        }
      }
    }
  }

  &.--3 {
    .entity__column {
      &.-label {
        padding-left: 50px;
      }

      &.-code {
        padding-left: 50px;
      }
    }

    .entity__grip {
      left: 40px;

      @include up-to-md {
        left: 20px;
      }
    }
  }

  &.-expanded {
    .entity__children,
    .entity__subjects {
      max-height: var(--max-height);
    }
  }

  &__type {
    background-color: $m_color_1;
    border-radius: $radius;
    padding: 2px 5px;
    font-weight: 700;
    margin-right: 5px;
  }

  &__row {
    display: flex;
    align-items: center;
    width: 100%;
    border-bottom: 1px dashed $m_color_6;
    position: relative;
    padding-left: 25px;

    @include dark-theme {
      color: $white;
      background-color: $m-color_18;
    }

    @include up-to-md {
      flex-wrap: wrap;
      padding: 5px 10px 5px 25px;
    }

    &:hover {
      background-color: $m_color_1;
    }
  }

  &__obl {
    display: inline-block;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid var(--opt-color);
    --opt-color: #00275e;
    position: relative;

    &:after {
      position: absolute;
      content: "";
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%) rotate(45deg);
      width: 8px;
      height: 8px;
      border: 1px solid var(--opt-color);
    }

    &.-opt {
      --opt-color: #f8a007;

      &:after {
        border-radius: 50%;
        background-color: var(--opt-color);
      }
    }
  }

  &__grip {
    position: absolute;
    left: 0;
    cursor: move;
    width: 25px;
    background: none;
    border: none;
    color: $black;
  }

  &__expand {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
  }

  &__column {
    width: 8%;
    padding: 10px;
    text-align: center;

    @include up-to-md {
      padding: 5px 0;
      text-align: left;
      width: 50%;
    }

    &.-label {
      width: 44%;
      text-align: left;
      @include up-to-md {
        width: 100%;
        font-weight: 700;
      }
    }

    &.-code {
      text-align: left;

      @include up-to-md {
        display: none;
      }
    }

    &.-ects {
      font-weight: 700;

      @include up-to-md {
        width: 30%;
      }
    }

    &.-coef,
    &.-hours {
      @include up-to-md {
        width: 30%;
      }
    }

    &.-obl {
      display: flex;
      align-items: center;
      justify-content: center;

      @include up-to-md {
        justify-content: flex-start;
        width: 30%;
      }
    }

    &.-type {
      @include up-to-md {
        width: 60%;
      }
    }
  }

  &__children,
  &__subjects {
    max-height: 0;
    overflow: hidden;
    transition: all 0.2s ease-in-out;

    &.-dragging:not(.sortable-chosen) {
      min-height: calc(var(--max-height) + 51px);
      border: 1px dashed $m_color_6;
      border-top: none;
    }
  }
}
</style>
